// Added by Sejune Oh on 2020-04-24
// Copyright (c) Coreline Soft, All rights are reserved.
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import { useState, useEffect } from 'react'
import _ from 'lodash'

// Helper
import LegacyEvtHandler from '@/helper/LegacyEvtHandler'
import Logger from '@/helper/Logger'

// UI
import { StatisticExportButton } from '@/components/worklist/StatisticExportButton'
import { StatisticsHistogram } from '@/components/worklist/StatisticsHistogram'

// Constants
import { EVT_STUDYLIST_SELECTION, EVT_UPDATE_CURRENT_DB } from '@/helper/define/legacyEventDefine'

// API
import {
  reqGetLCSReadingStudyCountByScore,
  reqGetTotalAndLCSReadingStudyCount,
} from '@/service/study/lcsStatistics.service'
import { reqGetSiteConfig } from '@/service/siteConfig.service'

// Interface
import { WebComponentProps } from '@/helper/registerWebComponent'
import { LCSStatisticStyleTypes } from './index'

const LCSStatisticWrapper = ({ classes, ...rest }: WebComponentProps) => {
  const [totalStudyCount, setTotalStudyCount] = useState(0)
  const [readingCount, setReadingCount] = useState(0)
  const [score1, setScore1] = useState(0)
  const [score2, setScore2] = useState(0)
  const [score3, setScore3] = useState(0)
  const [score4, setScore4] = useState(0)
  const [proxyToken, setProxyToken] = useState('')
  const [hasLCSData, setHasLCSData] = useState(false)
  const [guideLine, setGuideLine] = useState('')

  const getPositiveRate = () => {
    if (readingCount === 0) return 0

    return parseInt((((score3 + score4) / readingCount) * 100).toFixed(0))
  }
  const getSuspiciousRate = () => {
    if (readingCount === 0) return 0

    return parseInt(((score4 / readingCount) * 100).toFixed(0))
  }
  const setCountNRateWidthDataProxyToken = async (proxyToken: string) => {
    try {
      const {
        data: { study_count, reading_count },
      } = await reqGetTotalAndLCSReadingStudyCount({ proxyToken })
      const {
        data: { items },
      } = await reqGetLCSReadingStudyCountByScore({ proxyToken })
      const scoreSets = getCounts(items)
      const totalStudyCount = parseInt(study_count) || 0
      setHasLCSData(totalStudyCount !== 0)

      setProxyToken(proxyToken)
      setCounts({
        totalStudyCount,
        readingCount: parseInt(reading_count) || 0,
        ...scoreSets,
      })
    } catch (e) {
      Logger.errLog('Fail to get LCS Statistics data...')
      console.log(e)

      setHasLCSData(false)
      setCounts({})
    }
  }
  const getCounts = (readingStudyCountByScore: { score: string; count: string }[]) => {
    let score1 = 0
    let score2 = 0
    let score3 = 0
    let score4 = 0

    if (readingStudyCountByScore) {
      readingStudyCountByScore.map((v) => {
        switch (v.score) {
          case '1':
            score1 += parseInt(v.count)
            break
          case '2':
            score2 += parseInt(v.count)
            break
          case '3':
            score3 += parseInt(v.count)
            break
          case '4':
            score4 += parseInt(v.count)
            break
        }
        return v
      })
    }

    return {
      score1,
      score2,
      score3,
      score4,
    }
  }
  const setCounts = ({
    totalStudyCount = 0,
    readingCount = 0,
    score1 = 0,
    score2 = 0,
    score3 = 0,
    score4 = 0,
  }: {
    totalStudyCount?: number
    readingCount?: number
    score1?: number
    score2?: number
    score3?: number
    score4?: number
  }) => {
    setTotalStudyCount(totalStudyCount)
    setReadingCount(readingCount)
    setScore1(score1)
    setScore2(score2)
    setScore3(score3)
    setScore4(score4)
  }
  const fetchingStorageStatus = ({ detail }: any) => {
    setCountNRateWidthDataProxyToken(detail)
  }
  const updateStorageStatus = _.debounce(() => {
    setCountNRateWidthDataProxyToken(proxyToken)
  }, 500)
  const getGuideLine = async () => {
    const {
      data: { items },
    } = await reqGetSiteConfig()
    const filteredGuideLineValue = items.filter((item) => item.key === 'LungScreen.LCSGuideline')[0]?.value || 'Lung-RADS'
    setGuideLine(filteredGuideLineValue)
  }

  useEffect(() => {
    LegacyEvtHandler.addEvtListener(EVT_UPDATE_CURRENT_DB, fetchingStorageStatus)
    LegacyEvtHandler.addEvtListener(EVT_UPDATE_CURRENT_DB, getGuideLine)
    LegacyEvtHandler.addEvtListener(EVT_STUDYLIST_SELECTION, updateStorageStatus)

    return () => {
      LegacyEvtHandler.removeEvtListener(EVT_UPDATE_CURRENT_DB, fetchingStorageStatus)
      LegacyEvtHandler.removeEvtListener(EVT_UPDATE_CURRENT_DB, getGuideLine)
      LegacyEvtHandler.removeEvtListener(EVT_STUDYLIST_SELECTION, updateStorageStatus)
    }

    // eslint-disable-next-line
  }, [proxyToken])

  return (
    <div
      style={{
        display: 'flex',
        height: '46px',
        border: '1px solid #000',
        borderLeft: '2px solid #000',
      }}
    >
      <StatisticsHistogram
        score1={score1}
        score2={score2}
        score3={score3}
        score4={score4}
        guideLine={guideLine}
        positiveRate={getPositiveRate()}
        suspiciousRate={getSuspiciousRate()}
        readingCount={readingCount}
        totalCount={totalStudyCount}
        classes={classes as LCSStatisticStyleTypes}
      ></StatisticsHistogram>
      <StatisticExportButton
        proxyToken={proxyToken}
        hasLCSData={hasLCSData}
        classes={classes as LCSStatisticStyleTypes}
      />
    </div>
  )
}

export default LCSStatisticWrapper
